/*
 * wow-gallery.js — lamp page gallery.
 *
 * Phones (< 768px, swipe row): no lightbox. Tapping a slide that isn't fully
 * in view scrolls it into view (smooth); tapping a fully visible slide does
 * nothing. Dots below the row follow the scroll position and scroll to their
 * slide. Works with touch and with a mouse in a narrow window.
 *
 * Desktop (≥ 768px, 4-column row): clicking a photo opens the lightbox
 * (design system Lightbox): one image, ungraded, never cropped. ‹ › buttons and
 * the arrow keys step through the photos; ×, Escape or a click on the image or
 * background closes it and returns focus to the thumbnail.
 *
 * No visible strings here: all labels are rendered from locale keys into
 * aria-label / data-label-* attributes.
 */
(() => {
  const desktop = window.matchMedia('(min-width: 768px)');

  document.querySelectorAll('[data-wow-gallery]').forEach((root) => {
    const row = root.querySelector('[data-wow-gallery-row]');
    const slides = [...root.querySelectorAll('[data-wow-slide]')];
    const dots = [...root.querySelectorAll('[data-wow-dot]')];
    const wrap = root.closest('.wow');
    const box = wrap && wrap.querySelector('[data-wow-lightbox-dialog]');
    if (!row || !slides.length) return;

    // Taiga animates sections in with a transform, which would trap a
    // position:fixed overlay inside the section (under the header). Move the
    // lightbox to its own .wow host directly under <body>.
    if (box) {
      const host = document.createElement('div');
      host.className = 'wow wow-lightbox-host';
      document.body.appendChild(host);
      host.appendChild(box);
    }

    /* ---------- labels per mode ---------- */
    const setLabels = () => {
      const attr = desktop.matches ? 'data-label-open' : 'data-label-slide';
      slides.forEach((s) => s.setAttribute('aria-label', s.getAttribute(attr)));
    };
    setLabels();
    desktop.addEventListener('change', setLabels);

    /* ---------- phones: swipe row + dots ---------- */
    const offsetOf = (i) => slides[i].offsetLeft - slides[0].offsetLeft;
    const maxScroll = () => row.scrollWidth - row.clientWidth;
    const scrollToSlide = (i) => {
      row.scrollTo({ left: Math.min(offsetOf(i), maxScroll()), behavior: 'smooth' });
    };
    const fullyVisible = (el) => {
      const r = el.getBoundingClientRect();
      const rr = row.getBoundingClientRect();
      return r.left >= rr.left - 1 && r.right <= rr.right + 1;
    };
    const activeIndex = () => {
      if (row.scrollLeft >= maxScroll() - 2) return slides.length - 1;
      let best = 0;
      let dist = Infinity;
      slides.forEach((_, i) => {
        const d = Math.abs(offsetOf(i) - row.scrollLeft);
        if (d < dist) { dist = d; best = i; }
      });
      return best;
    };
    const updateDots = () => {
      const a = activeIndex();
      dots.forEach((d, i) => {
        if (i === a) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    };
    let ticking = false;
    row.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { ticking = false; updateDots(); });
    }, { passive: true });
    dots.forEach((d) => d.addEventListener('click', () => scrollToSlide(Number(d.getAttribute('data-wow-dot')))));
    window.addEventListener('resize', updateDots);

    /* ---------- desktop: lightbox ---------- */
    let current = 0;
    let opener = null;
    const img = box && box.querySelector('img');
    const closeBtn = box && box.querySelector('[data-wow-lightbox-close]');
    const prevBtn = box && box.querySelector('[data-wow-lightbox-prev]');
    const nextBtn = box && box.querySelector('[data-wow-lightbox-next]');
    const focusables = () => [prevBtn, nextBtn, closeBtn].filter(Boolean);

    const show = (i) => {
      current = (i + slides.length) % slides.length;
      img.src = slides[current].getAttribute('data-src');
    };
    const close = () => {
      box.hidden = true;
      img.removeAttribute('src');
      document.removeEventListener('keydown', onKey);
      if (opener) opener.focus();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(current - 1);
      else if (e.key === 'ArrowRight') show(current + 1);
      else if (e.key === 'Tab') {
        const f = focusables();
        const i = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    };
    const open = (i) => {
      if (!box) return;
      opener = slides[i];
      show(i);
      box.hidden = false;
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      document.addEventListener('keydown', onKey);
      closeBtn.focus();
    };

    if (box) {
      box.addEventListener('click', close);
      [prevBtn, nextBtn].forEach((b, n) => b && b.addEventListener('click', (e) => {
        e.stopPropagation();
        show(current + (n === 0 ? -1 : 1));
      }));
    }

    /* ---------- slide click: mode decides ---------- */
    slides.forEach((s, i) => s.addEventListener('click', () => {
      if (desktop.matches) { open(i); return; }
      if (!fullyVisible(s)) scrollToSlide(i);
    }));
  });
})();
